import { MorphemeGlyphTileGridView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import type {
  BatchedGraphemeTileData,
  GlyphStyle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import type { Level } from "@-/dev.improve.haniverse.lib.level";
import { cn } from "@-/com.npmjs.cn";
import {
  SurfaceContent,
  SurfaceDescription,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { type ReactNode, useMemo } from "react";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { LEVEL_SURFACE_DEFAULT_HREF } from "#level-surface-constants";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { NavigationFocusedRowContent } from "@-/dev.improve.haniverse.components.ui.header-scroll";

function buildSubtitle(level: Level, characters: readonly string[], completedLessonCount?: number) {
  // One square = one lesson: the card plus any slides that lead into or follow it.
  const totalLessons = arrayLength(characters);
  const lessonsLabel =
    completedLessonCount === undefined
      ? `${totalLessons} lessons`
      : `${completedLessonCount}/${totalLessons} lessons`;

  return level.slug === null ? lessonsLabel : `${level.slug} · ${lessonsLabel}`;
}

export type LevelSurfaceHeaderProps = {
  /** The lessons (surface squares) the level shows; defaults to its characters. */
  characters?: readonly string[];
  className?: string;
  /** When set, the subtitle shows progress as `completed/total lessons`. */
  completedLessonCount?: number;
  /** Replaces the `slug · N lessons` subtitle, e.g. a lesson card's own position in its level. */
  description?: string;
  /** The title; defaults to `Level N` (a pronunciation level passes `Level 2-P1`). */
  label?: string;
  level: Level;
  /**
   * When provided, the header strip becomes pressable. The levels browser uses this to open the
   * level-details sheet.
   */
  onPress?: () => void;
  trailing?: ReactNode;
};

/**
 * Off-color header strip of a level surface: "Level N" + subtitle. Designed to sit at the top of a
 * surrounding `Surface` (which owns the border + rounding); composes with one or more
 * `LevelSurfaceGlyphGrid` rows below it. When `onPress` is set the whole strip is a `Pressable`.
 */
export function LevelSurfaceHeader({
  characters: charactersProp,
  className,
  completedLessonCount,
  description: descriptionProp,
  label: labelProp,
  level,
  onPress,
  trailing,
}: LevelSurfaceHeaderProps) {
  const characters = charactersProp ?? level.characters;
  const label = labelProp ?? `Level ${level.index + 1}`;
  const description = descriptionProp ?? buildSubtitle(level, characters, completedLessonCount);
  const inner = (
    <>
      <SurfaceTitle>{label}</SurfaceTitle>
      <SurfaceDescription>{description}</SurfaceDescription>
    </>
  );

  if (trailing !== undefined) {
    // Equal inset on the trailing control's top, bottom, and right (matches Decomposition's
    // `p-1` header chrome). Drop the title/description's own vertical padding so the header
    // height is driven by the control + that shared inset, not by uneven text padding.
    const trailingInner = (
      <>
        <SurfaceTitle className="pt-0">{label}</SurfaceTitle>
        <SurfaceDescription className="pb-0">{description}</SurfaceDescription>
      </>
    );
    return (
      <SurfaceHeader className={cn("flex-row items-center gap-2 py-1 pr-1", className)}>
        {onPress === undefined ? (
          <View className="min-w-0 flex-1">{trailingInner}</View>
        ) : (
          <Pressable
            accessibilityLabel={label}
            accessibilityRole="button"
            className="min-w-0 flex-1"
            onPress={onPress}
            testID={`level-surface-header-${level.index + 1}`}
          >
            {trailingInner}
          </Pressable>
        )}
        {trailing}
      </SurfaceHeader>
    );
  }

  if (onPress === undefined) {
    return <SurfaceHeader className={className}>{inner}</SurfaceHeader>;
  }

  // A `Pressable` with `onPress` is exposed as an accessibility *button* on iOS, which
  // absorbs (and hides) its child `Text` nodes from the a11y tree. Without an explicit
  // `accessibilityLabel` the auto-aggregated button label is unreliable on iOS — the
  // "Level N" title text ends up unmatchable even though it's painted on screen (observed
  // in browse-mark-as-mastered.yaml: `tapOn: "Level 1"` → "Element not found"). Pin a
  // stable "Level N" label so the header stays targetable when it opens level details.
  return (
    <SurfaceHeader asChild className={className}>
      <Pressable
        accessibilityLabel={label}
        onPress={onPress}
        testID={`level-surface-header-${level.index + 1}`}
      >
        {inner}
      </Pressable>
    </SurfaceHeader>
  );
}

export type LevelSurfaceBodyProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Plain body wrapper that visually continues a `LevelSurfaceHeader`. Sits inside a surrounding
 * `Surface` which owns the border + rounding.
 */
export function LevelSurfaceBody({ children, className }: LevelSurfaceBodyProps) {
  return <SurfaceContent className={className}>{children}</SurfaceContent>;
}

export type LevelSurfaceGlyphGridProps = {
  /**
   * The caller's batched stage + pinyin-slice resolvers (`useBatchedGraphemeTileData`), lifted from
   * ONE query each for the whole list. Each tile reads its data from these and fires NO per-tile
   * query — collapsing what would otherwise be one stage `useQuery` + one change subscription + one
   * pinyin `useQueries` PER TILE (a JS-thread storm when a grid mounts hundreds of tiles at once,
   * serializing the whole page's queries behind it) into two batched queries at the parent.
   */
  batchedTileData: BatchedGraphemeTileData;
  className?: string;
  columns: number;
  /**
   * `grapheme → is the card version-complete?` map, lifted from a SINGLE batched query at the
   * caller (see `useCardFilterStaticFactsQueryOptions`). A grapheme absent from the map (or mapped
   * `false`) fades under the `fadeMissingMnemonicEnabled` device setting. Optional — grids that don't
   * surface authoring completion (e.g. the Learn deck) omit it and no tile fades.
   */
  completedByGrapheme?: ReadonlyMap<string, boolean>;
  /**
   * The level slot of this row's first tile, so `batchedTileData.getStage` is asked for the tile's
   * slot in the level (see `useLevelTileData`) rather than its column.
   */
  firstSlotIndex?: number;
  glyphs: readonly GraphemeGlyph[];
  /**
   * Pre-computed glyph size shared by every tile in this row. When set, tiles skip their per-cell
   * `onLayout` measurement pass. The list caller is responsible for deriving it from the row width
   * / column count.
   */
  glyphStyle?: GlyphStyle;
  href?: string;
  /** When true, adds bottom padding to close the surface. */
  isLastInLevel?: boolean;
  /** Let a virtualized page own one navigator for all mounted rows. */
  onNavigate?: (morpheme: string) => void;
  /** Laid over a grapheme's tile, e.g. a Browse level's slide-lesson dots. */
  renderTileOverlay?: (grapheme: string) => ReactNode;
  /** Pronunciation rows label each glyph with the reading taught by its card. */
  showPrimaryPinyin?: boolean;
  /**
   * Plain (not tone-coloured) glyphs: a meaning-only level's cards, whose pronunciations the
   * learner has not met yet.
   */
  uncolored?: boolean;
};

/** Character row using the shared grid renderer and the caller's batched data. */
export function LevelSurfaceGlyphGrid({
  batchedTileData,
  className,
  columns,
  completedByGrapheme,
  firstSlotIndex = 0,
  glyphs,
  glyphStyle,
  href = LEVEL_SURFACE_DEFAULT_HREF,
  isLastInLevel = false,
  onNavigate,
  renderTileOverlay,
  showPrimaryPinyin,
  uncolored,
}: LevelSurfaceGlyphGridProps) {
  const rowTileData = useMemo<BatchedGraphemeTileData>(
    () =>
      firstSlotIndex === 0
        ? batchedTileData
        : {
            getSlice: (grapheme, index) =>
              batchedTileData.getSlice(
                grapheme,
                index === undefined ? undefined : firstSlotIndex + index,
              ),
            getStage: (grapheme, index) =>
              batchedTileData.getStage(
                grapheme,
                index === undefined ? undefined : firstSlotIndex + index,
              ),
          },
    [batchedTileData, firstSlotIndex],
  );
  return (
    <View className={cn("px-1 pt-1", isLastInLevel && "pb-1", className)}>
      <NavigationFocusedRowContent
        key={glyphs.map((glyph) => glyph.original).join(",")}
        testID="level-surface-glyph-row-shell"
      >
        <MorphemeGlyphTileGridView
          columns={columns}
          gap={4}
          glyphStyle={glyphStyle}
          graphemeList={glyphs}
          href={href}
          isMissingMnemonic={(grapheme) =>
            completedByGrapheme !== undefined && completedByGrapheme.get(grapheme) !== true
          }
          onNavigate={onNavigate}
          renderTileOverlay={renderTileOverlay}
          showPrimaryPinyin={showPrimaryPinyin}
          tileData={rowTileData}
          uncolored={uncolored}
        />
      </NavigationFocusedRowContent>
    </View>
  );
}
